import {useEffect, useRef} from 'react';

import type {PagePromptContent} from '../store/thread';
import {pageContentStore} from './store';

// NOTE: @types/react is pinned at v17 in this app, which predates 'useId'. Use
// a module-level counter to create a stable id per hook instance.
let nextSliceId = 0;

/**
 * usePageContentSlice registers `slice` as this component's contribution to the
 * page content of variant `type` for the duration of its mount. Pass `null` to
 * skip the contribution without violating the rules of hooks.
 */
export function usePageContentSlice<V extends PagePromptContent>(
  type: V['type'],
  slice: Partial<Omit<V, 'type'>> | null
): void {
  const idRef = useRef<number>();
  if (idRef.current === undefined) {
    idRef.current = nextSliceId++;
  }
  const id = idRef.current;

  // NOTE: Use a ref so the store reads the latest value without re-running
  // the effect on every slice change.
  const sliceRef = useRef(slice);
  sliceRef.current = slice;

  useEffect(() => {
    pageContentStore.set(id, type, () => sliceRef.current);
    return () => pageContentStore.remove(id);
  }, [id, type]);
}
